<template>
  <CustomerServicePortalLayout
    portalType="customer" 
    title="Policy Changes"
    description="We're here to assist you with all your service needs."
    :icon="policyChangesIcon"
    :backRoute="{ name: 'customerServicePage' }"
  >
    <!--Service Options Cards-->
    <div class="flex flex-wrap justify-center gap-6">
      <div
        class="card-customer flex-grow max-w-md rounded-lg shadow-md p-6 text-center border border-gray-200 hover:shadow-lg transition-shadow"
      >
        <img
          :src="mortgageeChangesIcon"
          alt="Mortgagee Changes"
          class="h-20 mb-4 mx-auto"
          loading="lazy"
        />
        <h2 class="text-lg font-semibold text-gray-700 mb-3">
          Mortgagee Changes
        </h2>
        <p class="text-gray-600 mb-5">
          Add, update, or remove mortgagee clauses.
        </p>
        <router-link 
          :to="{ name: 'customerService.policyChanges.mortgagee' }"
          class="btn-customer"
        >
          Make Changes
        </router-link>
      </div>
      <div
        class="card-customer flex-grow max-w-md rounded-lg shadow-md p-6 text-center border border-gray-200 hover:shadow-lg transition-shadow"
      >
        <img
          :src="homeInsuranceChangesIcon"
          alt="Home Insurance Changes"
          class="h-20 mb-4 mx-auto"
          loading="lazy"
        />
        <h2 class="text-lg font-semibold text-gray-700 mb-3">
          Home Insurance Changes
        </h2>
        <p class="text-gray-600 mb-5">
          Request changes for your home insurance policy.
        </p>
        <router-link 
          :to="{ name: 'customerService.policyChanges.home' }"
          class="btn-customer"
        >
          Make Changes
        </router-link>
      </div>
      <div
        class="card-customer flex-grow max-w-md rounded-lg shadow-md p-6 text-center border border-gray-200 hover:shadow-lg transition-shadow"
      >
        <img
          :src="policySearchIcon"
          alt="Policy Search"
          class="h-20 mb-4 mx-auto"
          loading="lazy"
        />
        <h2 class="text-lg font-semibold text-gray-700 mb-3">Policy Search</h2>
        <p class="text-gray-600 mb-5">
          Don't have your policy number? No worries! Use our helpful policy
          search tool.
        </p>
        <a 
          href="/customerservice/policy-search"
          class="btn-customer inline-block"
          @click.prevent="navigateToPolicySearch"
        >
          Find Policy
        </a>
      </div>
      <div
        class="card-customer flex-grow max-w-md rounded-lg shadow-md p-6 text-center border border-gray-200 hover:shadow-lg transition-shadow"
      >
        <img
          :src="cancellationRequestIcon"
          alt="Cancellation Request"
          class="h-20 mb-4 mx-auto"
          loading="lazy"
        />
        <h2 class="text-lg font-semibold text-gray-700 mb-3">
          Cancellation Request
        </h2>
        <p class="text-gray-600 mb-5">
          Use our helpful online form to streamline policy cancellation
          requests.
        </p>
        <router-link 
          :to="{ name: 'customerService.policyChanges.cancellation' }"
          class="btn-customer"
        >
          Make Request
        </router-link>
      </div>
      <!-- Simple Auto Changes Card -->
      <div
        class="card-customer flex-grow max-w-md rounded-lg shadow-md p-6 text-center border border-gray-200 hover:shadow-lg transition-shadow"
      >
        <img
          :src="policyChangesIcon"
          alt="Auto Policy Changes"
          class="h-20 mb-4 mx-auto"
          loading="lazy"
        />
        <h2 class="text-lg font-semibold text-gray-700 mb-3">
          Auto Policy Changes
        </h2>
        <p class="text-gray-600 mb-5">
          Request changes to your personal auto insurance policy.
        </p>
        <router-link 
          :to="{ name: 'customerService.policyChanges.auto' }"
          class="btn-customer"
        >
          Make Changes
        </router-link>
      </div>
    </div>
    <!-- Router view replaces conditional component rendering -->
  </CustomerServicePortalLayout>
</template>

<script setup>
import { Icon } from '@iconify/vue';
import { onMounted } from 'vue';
import { useRouter } from 'vue-router';
import CustomerServicePortalLayout from '@/components/layout/CustomerServicePortalLayout.vue';

// Import icons
import mortgageeChangesIcon from '@/assets/images/ui/icons/mortgageeChanges.svg';
import homeInsuranceChangesIcon from '@/assets/images/ui/icons/homeInsuranceChanges.svg';
import policySearchIcon from '@/assets/images/ui/icons/policySearch.svg';
import cancellationRequestIcon from '@/assets/images/ui/icons/cancellationRequest.svg';
import policyChangesIcon from '@/assets/images/ui/icons/policyChanges.svg';

const router = useRouter();

// Navigation function for policy search with error handling
const navigateToPolicySearch = () => {
  try {
    console.log('Attempting to navigate to policy search page...');
    
    // Directly look for the exact route by name
    const policySearchRoute = router.getRoutes().find(route => 
      route.name === 'customerService.policySearch'
    );
    
    console.log('Found policy search route:', policySearchRoute);
    
    if (policySearchRoute) {
      console.log('Navigating to route:', policySearchRoute.path);
      router.push({ name: 'customerService.policySearch' })
        .catch(err => {
          console.error('Failed to navigate using route name, error:', err);
          window.location.href = '/customerservice/policy-search';
        });
    } else {
      console.log('Route not found, falling back to direct path navigation');
      window.location.href = '/customerservice/policy-search';
    }
  } catch (err) {
    console.error('Navigation error:', err);
    // Last resort fallback
    window.location.href = '/customerservice/policy-search';
  }
};

onMounted(() => {
  window.scrollTo(0, 0);
  
  // Debug logging for routes
  console.log('=== Router Debugging ===');
  console.log('Available routes:', router.getRoutes().map(route => ({ 
    name: route.name, 
    path: route.path
  })));
  console.log('Current route:', router.currentRoute.value);
  
  // Look for exact policy search route by name
  const policySearchRoute = router.getRoutes().find(route => 
    route.name === 'customerService.policySearch'
  );
  console.log('Policy search route (exact match):', policySearchRoute);
  
  // Also perform a less strict search
  const policySearchRoutes = router.getRoutes().filter(route => 
    route.name?.toString().toLowerCase().includes('policy') ||
    route.path?.includes('policy')
  );
  console.log('Any policy-related routes:', policySearchRoutes.map(route => ({ 
    name: route.name, 
    path: route.path 
  })));
  
  // Verify all routes used in this component
  console.log('=== Route Verification ===');
  const routeChecks = [
    { name: 'customerService.policyChanges.mortgagee', label: 'Mortgagee Changes' },
    { name: 'customerService.policyChanges.home', label: 'Home Insurance Changes' },
    { name: 'customerService.policySearch', label: 'Policy Search' },
    { name: 'customerService.policyChanges.cancellation', label: 'Cancellation Request' },
    { name: 'customerService.policyChanges.auto', label: 'Auto Policy Changes' }
  ];
  
  routeChecks.forEach(routeCheck => {
    const route = router.getRoutes().find(r => r.name === routeCheck.name);
    console.log(`Route "${routeCheck.label}": `, route ? 
      `Found - ${route.path}` : 
      'NOT FOUND - This might cause navigation errors');
  });
});
</script>

<style scoped></style>
